import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { api } from "@/api/client";
import type {
  DirectionalMove,
  RawBin,
  RunAnalysisRequest,
  RunAnalysisResponse,
} from "@/api/types";
import type { Tick } from "@/hooks/useTickStream";
import { DEFAULT_TAB_ID, addTab, removeTab } from "./tabsSlice";
import { hydrateWorkspace } from "./workspacesSlice";
import { enterReplay } from "./replaySlice";
import { tsToDateTimeLocal } from "@/utils/chartTime";

export interface AnalysisTabState {
  rawBins: RawBin[];
  /** Live-only volume profile, scoped to the current live session.
   *  Mirrors `rawBins` for the additions made by `addLiveTicks` —
   *  reset to [] on session start, but never receives the prefetch's
   *  contribution. Rendered as a separate overlay on top of `rawBins`
   *  so live activity is visible even when the prefetched peaks
   *  dwarf the live increments. */
  liveBins: RawBin[];
  directionalMove: DirectionalMove | null;
  liquidityRanges: unknown[];
  leastTouchRanges: unknown[];
  fromCache: boolean;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;

  // Per-tab live snapshot. `liveTabId` at slice root is authoritative
  // for "which tab owns the live session" — `isLive` here just makes
  // it cheap to read in selectors.
  isLive: boolean;
  liveStartedAt: number | null; // unix ms
  /** Count of ticks ingested by `addLiveTicks` since the last
   *  start/clear. Exposed so the UI can show "live: +N ticks". */
  liveTickCount: number;

  /** Period this tab's analysis covers. Captured at run time so the
   *  chart can label *what the rawBins represent*, independent of the
   *  Header's time pickers (which the user may have edited since).
   *  Null until an analysis runs; cleared by clearAnalysis. */
  analyzedFor: {
    /** Wall-clock string in `analysisStart`'s format ("YYYY-MM-DDTHH:mm").
     *  For pure live (no prefetch), `start` is the click moment. */
    start: string;
    /** End of the prefetched / static range. For pure live this
     *  equals `start` (no historical range yet). */
    end: string;
    /** IANA tz the start/end strings refer to. Optional — older
     *  sessions or live-only mode may omit it. */
    timezone?: string;
    /** "static" = fixed range, "live-prefetch" = historical fill +
     *  ongoing live append, "live" = pure live from `start`. Drives
     *  how the period is rendered ("→ end" vs "→ now (live)"). */
    mode: "static" | "live-prefetch" | "live";
  } | null;
}

interface AnalysisState {
  byTab: Record<string, AnalysisTabState>;
  /** Single-live rule: at most one tab may be in live mode at a
   *  time. Starting Live on tab B stops it on tab A. The
   *  useLiveAnalysis hook reads this to know which tab's symbol to
   *  subscribe to. */
  liveTabId: string | null;
}

function createDefaultAnalysisTabState(): AnalysisTabState {
  return {
    rawBins: [],
    liveBins: [],
    directionalMove: null,
    liquidityRanges: [],
    leastTouchRanges: [],
    fromCache: false,
    status: "idle",
    error: null,
    isLive: false,
    liveStartedAt: null,
    liveTickCount: 0,
    analyzedFor: null,
  };
}

const initialState: AnalysisState = {
  byTab: { [DEFAULT_TAB_ID]: createDefaultAnalysisTabState() },
  liveTabId: null,
};

function getTab(state: AnalysisState, tabId: string): AnalysisTabState {
  let t = state.byTab[tabId];
  if (!t) {
    t = createDefaultAnalysisTabState();
    state.byTab[tabId] = t;
  }
  return t;
}

/** Helper: enforce single-live rule. Clears isLive on any tab other
 *  than `keepTabId` and updates the slice's `liveTabId`. */
function setLive(state: AnalysisState, keepTabId: string | null): void {
  for (const [id, t] of Object.entries(state.byTab)) {
    if (id !== keepTabId && t.isLive) {
      t.isLive = false;
      t.liveStartedAt = null;
    }
  }
  state.liveTabId = keepTabId;
}

// Volume-profile bin size for live aggregation. Matches the default
// in scripts/run_directional_analysis.py so live and static analyses
// produce comparable bin layouts.
const BIN_SIZE = 0.5;

function binFor(mid: number): number {
  // MUST match the server's bin-center convention in
  // pulseengine/directional/liquidity_counter.py:
  //   key   = floor(mid / bin_size) * bin_size          # bin floor
  //   price = round(key + bin_size / 2.0, 6)            # bin center
  // Bin centers therefore land on …0.25 / …0.75 (not …0 / …0.5).
  const key = Math.floor(mid / BIN_SIZE) * BIN_SIZE;
  return Math.round((key + BIN_SIZE / 2) * 1_000_000) / 1_000_000;
}

export interface RunAnalysisArg {
  tabId: string;
  request: RunAnalysisRequest;
}

export const runAnalysis = createAsyncThunk<
  { response: RunAnalysisResponse; tabId: string },
  RunAnalysisArg
>("analysis/run", async (arg) => {
  const response = await api.runAnalysis(arg.request);
  return { response, tabId: arg.tabId };
});

/**
 * Combined mode: live mode flips on the *instant* the user clicks Run
 * Analysis (no waiting for the server), the prefetch thunk fires in
 * parallel, and when its response arrives the bins it produced are
 * **merged** into the bins live mode already started accumulating.
 */
export const startLiveWithPrefetch = createAsyncThunk<
  { response: RunAnalysisResponse; tabId: string },
  RunAnalysisArg
>("analysis/startLiveWithPrefetch", async (arg) => {
  const response = await api.runAnalysis(arg.request);
  return { response, tabId: arg.tabId };
});

const analysisSlice = createSlice({
  name: "analysis",
  initialState,
  reducers: {
    clearAnalysis(state, action: PayloadAction<{ tabId: string }>) {
      const fresh = createDefaultAnalysisTabState();
      state.byTab[action.payload.tabId] = fresh;
      if (state.liveTabId === action.payload.tabId) {
        state.liveTabId = null;
      }
    },

    /** Begin a live analyze session ON TOP of whatever analysis is
     * already on screen. Preserves `rawBins` and `directionalMove`
     * so live ticks extend the existing volume profile. */
    startLiveAnalysis(
      state,
      action: PayloadAction<{ tabId: string; startedAt: number }>,
    ) {
      const t = getTab(state, action.payload.tabId);
      t.status = "succeeded";
      t.error = null;
      t.isLive = true;
      t.liveStartedAt = action.payload.startedAt;
      t.liveTickCount = 0;
      t.liveBins = [];
      // Pure live (no historical prefetch): label the period as
      // "live since click moment", in UTC like the rest of the display.
      const stamp = tsToDateTimeLocal(
        Math.floor(action.payload.startedAt / 1000),
      );
      t.analyzedFor = { start: stamp, end: stamp, mode: "live" };
      setLive(state, action.payload.tabId);
    },

    /** Stop accumulating but keep the visualization on screen. */
    stopLiveAnalysis(state, action: PayloadAction<{ tabId: string }>) {
      const t = getTab(state, action.payload.tabId);
      t.isLive = false;
      if (state.liveTabId === action.payload.tabId) {
        state.liveTabId = null;
      }
    },

    /** Fold a batch of ticks into the live tab's rawBins /
     *  directionalMove. The tabId comes from the slice's liveTabId,
     *  not from the caller — the live-stream hook just hands us ticks
     *  and we route them to whichever tab owns the session. */
    addLiveTicks(state, action: PayloadAction<Tick[]>) {
      const tabId = state.liveTabId;
      if (!tabId) return;
      const t = state.byTab[tabId];
      if (!t || !t.isLive || action.payload.length === 0) return;

      t.liveTickCount += action.payload.length;

      // Build a price → index lookup over the existing bins so we can
      // increment in O(1) per tick. Parallel lookup for the live-only
      // overlay (`liveBins`).
      const idx = new Map<number, number>();
      t.rawBins.forEach((b, i) => idx.set(b.price, i));
      const liveIdx = new Map<number, number>();
      t.liveBins.forEach((b, i) => liveIdx.set(b.price, i));

      let start = t.directionalMove?.start_price ?? null;
      let end = t.directionalMove?.end_price ?? null;
      let hi = t.directionalMove?.high ?? -Infinity;
      let lo = t.directionalMove?.low ?? Infinity;
      let total = t.directionalMove?.total_ticks ?? 0;

      for (const tick of action.payload) {
        const mid = (tick.bid + tick.ask) / 2;
        const price = binFor(mid);

        const i = idx.get(price);
        if (i !== undefined) {
          t.rawBins[i].tick_count += 1;
        } else {
          t.rawBins.push({ price, tick_count: 1 });
          idx.set(price, t.rawBins.length - 1);
        }

        const li = liveIdx.get(price);
        if (li !== undefined) {
          t.liveBins[li].tick_count += 1;
        } else {
          t.liveBins.push({ price, tick_count: 1 });
          liveIdx.set(price, t.liveBins.length - 1);
        }

        if (start === null) start = mid;
        end = mid;
        if (mid > hi) hi = mid;
        if (mid < lo) lo = mid;
        total += 1;
      }

      const delta = (end ?? 0) - (start ?? 0);
      t.directionalMove = {
        direction: delta > 0 ? "bullish" : delta < 0 ? "bearish" : "flat",
        start_price: start ?? 0,
        end_price: end ?? 0,
        delta_price: delta,
        delta_pct: start ? (delta / start) * 100 : 0,
        high: hi,
        low: lo,
        total_ticks: total,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addTab, (state, action) => {
        if (!state.byTab[action.payload.id]) {
          state.byTab[action.payload.id] = createDefaultAnalysisTabState();
        }
      })
      .addCase(removeTab, (state, action) => {
        delete state.byTab[action.payload];
        if (state.liveTabId === action.payload) state.liveTabId = null;
      })
      // Entering replay stops Live Analyze on that tab. Live bins are folded
      // from the tick stream at the real "now"; replay moves the chart's now
      // into the past, and a live overlay on a replayed chart describes a
      // moment that isn't on screen. Owned here rather than in the Chart so
      // the invariant holds however replay was entered.
      .addCase(enterReplay, (state, action) => {
        const t = state.byTab[action.payload.tabId];
        if (t?.isLive) {
          t.isLive = false;
          t.liveStartedAt = null;
        }
        if (state.liveTabId === action.payload.tabId) state.liveTabId = null;
      })
      .addCase(hydrateWorkspace, (state, action) => {
        // Workspace deliberately doesn't persist analysis results
        // (rawBins / directionalMove etc.) — they're stale by the
        // time the user reloads. Drop liveTabId for the same reason:
        // a live session is ephemeral, never resumes on reload.
        const next: Record<string, AnalysisTabState> = {};
        for (const tabId of Object.keys(action.payload.byTab)) {
          next[tabId] = createDefaultAnalysisTabState();
        }
        state.byTab = next;
        state.liveTabId = null;
      })

      .addCase(runAnalysis.pending, (state, action) => {
        const t = getTab(state, action.meta.arg.tabId);
        t.status = "loading";
        t.error = null;
      })
      .addCase(runAnalysis.fulfilled, (state, action) => {
        const { response, tabId } = action.payload;
        const t = getTab(state, tabId);
        if (!response.success) {
          t.status = "failed";
          t.error = response.error ?? "Analysis failed";
          return;
        }
        t.status = "succeeded";
        t.rawBins = response.raw_bins ?? [];
        t.liveBins = [];
        t.directionalMove = response.directional_move ?? null;
        t.liquidityRanges = response.liquidity_ranges ?? [];
        t.leastTouchRanges = response.least_touch_ranges ?? [];
        t.fromCache = response.from_cache ?? false;
        // Static run replaces state — stop any live accumulation on this tab.
        t.isLive = false;
        t.liveStartedAt = null;
        if (state.liveTabId === tabId) state.liveTabId = null;
        // Stamp the period these bins cover, in the same wall-clock
        // strings the user typed into the Header pickers, so the chart
        // can label it independent of any subsequent picker edits.
        const req = action.meta.arg.request;
        t.analyzedFor = {
          start: req.start_time,
          end: req.end_time,
          timezone: req.timezone,
          mode: "static",
        };
      })
      .addCase(runAnalysis.rejected, (state, action) => {
        const t = getTab(state, action.meta.arg.tabId);
        t.status = "failed";
        t.error = action.error.message ?? "Failed to run analysis";
      })
      .addCase(startLiveWithPrefetch.pending, (state, action) => {
        // Wipe and immediately enter live mode so the chart starts
        // drawing the live volume profile from t=0 (the click). The
        // server prefetch runs in parallel — its result lands via
        // `.fulfilled` below and gets *merged* into the bins live has
        // already accumulated.
        const tabId = action.meta.arg.tabId;
        const t = getTab(state, tabId);
        t.rawBins = [];
        t.directionalMove = null;
        t.liquidityRanges = [];
        t.leastTouchRanges = [];
        t.fromCache = false;
        t.status = "loading";
        t.error = null;
        t.isLive = true;
        t.liveStartedAt = Date.now();
        t.liveTickCount = 0;
        // Period label = the request's [start, "now-at-click"] range,
        // with "+ live" appended in the renderer. `mode` distinguishes
        // this from a static run that may have the same range strings.
        const req = action.meta.arg.request;
        t.analyzedFor = {
          start: req.start_time,
          end: req.end_time,
          timezone: req.timezone,
          mode: "live-prefetch",
        };
        setLive(state, tabId);
      })
      .addCase(startLiveWithPrefetch.fulfilled, (state, action) => {
        const { response, tabId } = action.payload;
        const t = getTab(state, tabId);
        if (!response.success) {
          t.status = "failed";
          t.error = response.error ?? "Prefetch analysis failed";
          // Leave isLive alone — live mode keeps running even if the
          // historical fill-in failed; the user can retry.
          return;
        }
        t.status = "succeeded";
        t.liquidityRanges = response.liquidity_ranges ?? [];
        t.leastTouchRanges = response.least_touch_ranges ?? [];
        t.fromCache = response.from_cache ?? false;

        // Merge the server's bins into the live-accumulated bins.
        // Sum tick_counts per price (binFor convention is identical
        // server-side and client-side; that's why this O(n) merge is
        // safe).
        const serverBins = response.raw_bins ?? [];
        const idx = new Map<number, number>();
        t.rawBins.forEach((b, i) => idx.set(b.price, i));
        for (const sb of serverBins) {
          const existing = idx.get(sb.price);
          if (existing !== undefined) {
            t.rawBins[existing].tick_count += sb.tick_count;
          } else {
            t.rawBins.push({ price: sb.price, tick_count: sb.tick_count });
            idx.set(sb.price, t.rawBins.length - 1);
          }
        }

        // Merge directionalMove. Server covers `[start, click]`, live
        // covers `[click, now]` — combined the move spans the union.
        const sd = response.directional_move;
        const ld = t.directionalMove;
        if (sd) {
          const start = sd.start_price ?? ld?.start_price ?? 0;
          const end = ld?.end_price ?? sd.end_price ?? 0;
          const hi = Math.max(sd.high ?? -Infinity, ld?.high ?? -Infinity);
          const lo = Math.min(sd.low ?? Infinity, ld?.low ?? Infinity);
          const total = (sd.total_ticks ?? 0) + (ld?.total_ticks ?? 0);
          const delta = end - start;
          t.directionalMove = {
            direction: delta > 0 ? "bullish" : delta < 0 ? "bearish" : "flat",
            start_price: start,
            end_price: end,
            delta_price: delta,
            delta_pct: start ? (delta / start) * 100 : 0,
            high: hi,
            low: lo,
            total_ticks: total,
          };
        }
      })
      .addCase(startLiveWithPrefetch.rejected, (state, action) => {
        const t = getTab(state, action.meta.arg.tabId);
        t.status = "failed";
        t.error = action.error.message ?? "Failed to run prefetch analysis";
        // Leave isLive alone — live mode continues regardless.
      });
  },
});

export const {
  clearAnalysis,
  startLiveAnalysis,
  stopLiveAnalysis,
  addLiveTicks,
} = analysisSlice.actions;
export default analysisSlice.reducer;
